import { useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { ROUTES } from '../../routes';

/**
 * Guards a route by role. Place inside a layout-level auth guard so the
 * token check has already run; this component only checks role membership.
 *
 * allowedRoles: null  → any authenticated user passes through
 * allowedRoles: [...] → user.role must be in the list
 *
 * Role-specific redirect rules:
 *  - broker trying PB-only pages → /broker-dashboard with toast
 *  - PB/deputy_pb trying /broker-dashboard → /dashboard
 */
export default function RequireRole({ allowedRoles, superAdminOnly, children }) {
  const { auth, effectiveUser } = useContext(AuthContext);
  const location = useLocation();

  if (!auth.token) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  const role = effectiveUser?.role;

  // Batch H (H1): routes flagged superAdminOnly. During View As the viewed
  // user is never a super admin, so these routes are closed there too.
  if (superAdminOnly && !effectiveUser?.isSuperAdmin) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(role)) {
    // Firm admin trying to access any restricted area → redirect to firm dashboard
    if (role === 'firm_admin') {
      return <Navigate to={ROUTES.FIRM_DASHBOARD} replace />;
    }
    // Broker trying to access a PB-only area → redirect to their dashboard with toast
    if (role === 'broker') {
      return (
        <Navigate
          to={ROUTES.BROKER_DASHBOARD}
          replace
          state={{ message: 'That area is for Principal Brokers only.' }}
        />
      );
    }
    // PB/deputy_pb trying to access broker-only area → redirect to PB dashboard
    if (['principal_broker', 'deputy_pb'].includes(role) &&
        location.pathname === ROUTES.BROKER_DASHBOARD) {
      return <Navigate to={ROUTES.DASHBOARD} replace />;
    }
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return children;
}
